import React from "react";
import { TrendingUp } from "lucide-react";
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartDataSkeleton } from "../../../components/common/Skeleton";
import {
  DashboardTimeFilter,
  DashboardTrend,
  UserGrowthChartPoint,
} from "../types/dashboard-ui.types";
import { useTranslation } from "react-i18next";
import { DashboardErrorState } from "./DashboardErrorState";

interface DashboardUserGrowthChartProps {
  loading: boolean;
  trend: DashboardTrend;
  timeFilter: DashboardTimeFilter;
  chartData: UserGrowthChartPoint[];
  error: string | null;
  onRetry: () => void;
}

interface GrowthDotProps {
  cx?: number;
  cy?: number;
  payload?: UserGrowthChartPoint;
  index?: number;
}

interface GrowthLabelProps {
  x?: number;
  y?: number;
  value?: string | number;
  index?: number;
}

interface GrowthTooltipProps {
  active?: boolean;
  payload?: Array<{ value?: number | string }>;
  label?: string | number | Date;
}

interface GrowthTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: string | number | Date };
}

const resolveDotColor = (payload: UserGrowthChartPoint | undefined, index: number | undefined): string => {
  if (index === 0 || !payload?.change) return "#3B82F6";
  return payload.change.startsWith("-") ? "#ef4444" : "#22c55e";
};

const CustomDot: React.FC<GrowthDotProps> = (props) => {
  const { cx, cy, payload, index } = props;
  if (cx === undefined || cy === undefined || !payload) return null;

  return <circle cx={cx} cy={cy} r={6} fill={resolveDotColor(payload, index)} stroke="#fff" strokeWidth={2} />;
};

const CustomActiveDot: React.FC<GrowthDotProps> = (props) => {
  const { cx, cy, payload, index } = props;
  if (cx === undefined || cy === undefined || !payload) return null;

  return <circle cx={cx} cy={cy} r={8} fill={resolveDotColor(payload, index)} stroke="#fff" strokeWidth={2} />;
};

const GrowthLabelContent: React.FC<GrowthLabelProps> = (props) => {
  const { x, y, value, index } = props;
  if (x === undefined || y === undefined || !value || index === 0) return null;

  const text = String(value);
  const backgroundColor = text.startsWith("-") ? "#ef4444" : "#22c55e";

  return (
    <g>
      <rect x={x - 25} y={y - 35} width="50" height="20" rx="10" fill={backgroundColor} stroke="none" />
      <text x={x} y={y - 20} textAnchor="middle" fill="white" fontSize="10" fontWeight="700">
        {text}
      </text>
    </g>
  );
};

const GrowthTooltipContent: React.FC<GrowthTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  const date = new Date(label?.toString() || "");
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const isDarkMode = document.documentElement.classList.contains("dark");

  return (
    <div
      style={{
        borderRadius: 12,
        fontSize: 14,
        backgroundColor: isDarkMode ? "#1f2937" : "#fff",
        border: isDarkMode ? "1px solid #374151" : "1px solid #e5e7eb",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        padding: "8px 12px",
      }}
    >
      <div style={{ fontWeight: 600, color: isDarkMode ? "#f3f4f6" : "#111827" }}>
        {formattedDate}
      </div>
      <div style={{ fontWeight: 600, color: "#3B82F6" }}>
        Users: {payload[0].value}
      </div>
    </div>
  );
};

const GrowthTickContent: React.FC<GrowthTickProps & { timeFilter: DashboardTimeFilter }> = ({
  x,
  y,
  payload,
  timeFilter,
}) => {
  if (x === undefined || y === undefined || payload?.value === undefined) return null;

  const date = new Date(payload.value);
  const isDarkMode = document.documentElement.classList.contains("dark");
  const fill = isDarkMode ? "#9ca3af" : "#6b7280";

  if (timeFilter === "this_month") {
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={12} textAnchor="middle" fill={fill} fontSize="13">
          <tspan x="0" dy="0">{month}</tspan>
          <tspan x="0" dy="12">{year}</tspan>
        </text>
      </g>
    );
  }

  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={12} textAnchor="middle" fill={fill} fontSize="13">
        <tspan x="0" dy="0">{month}</tspan>
        <tspan x="0" dy="12">{day}</tspan>
      </text>
    </g>
  );
};

export const DashboardUserGrowthChart: React.FC<DashboardUserGrowthChartProps> = ({
  loading,
  trend,
  timeFilter,
  chartData,
  error,
  onRetry,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <div
          className="flex items-center gap-2 mb-4 text-[15px] text-[#6E7670] dark:text-gray-200"
          style={{ fontFamily: "Cal Sans" }}
        >
          {t("dashboard.charts.user_growth")}
        </div>
        <ChartDataSkeleton />
      </div>
    );
  }

  if (error && chartData.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <div
          className="flex items-center gap-2 mb-4 text-[15px] text-[#6E7670] dark:text-gray-200"
          style={{ fontFamily: "Cal Sans" }}
        >
          {t("dashboard.charts.user_growth")}
        </div>
        <DashboardErrorState message={error} onRetry={onRetry} className="border-none py-8" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
      <div
        className="flex items-center gap-2 mb-4 text-[15px] text-[#6E7670] dark:text-gray-200"
        style={{ fontFamily: "Cal Sans" }}
      >
        {t("dashboard.charts.user_growth")}
      </div>
      {trend && (
        <div className="flex gap-4 mb-4 w-full">
          <div
            className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-md w-fit ${
              trend.trend === "decline"
                ? "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700"
                : "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700"
            }`}
          >
            <TrendingUp
              className={`h-4 w-4 ${
                trend.trend === "decline"
                  ? "text-red-600 dark:text-red-400"
                  : "text-emerald-600 dark:text-emerald-400"
              }`}
            />
            {trend.trend === "decline" ? (
              trend.severity === "strong" ? (
                t("dashboard.charts.growth_trends.strong_decline", {
                  monthly: trend.monthlyGrowth.toFixed(1),
                })
              ) : trend.severity === "moderate" ? (
                t("dashboard.charts.growth_trends.moderate_decline", {
                  monthly: trend.monthlyGrowth.toFixed(1),
                })
              ) : (
                t("dashboard.charts.growth_trends.mild_decline", {
                  monthly: trend.monthlyGrowth.toFixed(1),
                })
              )
            ) : (
              t("dashboard.charts.growth_trends.positive", {
                monthly: trend.monthlyGrowth.toFixed(1),
              })
            )}
          </div>

          <div
            className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-md w-fit ${
              trend.averageWeeklyGrowth >= 0
                ? "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20"
                : "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700"
            }`}
            style={
              trend.averageWeeklyGrowth >= 0 ? { border: "1px solid #A4F4CF" } : {}
            }
          >
            <TrendingUp
              className={`h-4 w-4 ${
                trend.averageWeeklyGrowth >= 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            />
            {trend.averageWeeklyGrowth >= 0
              ? t("dashboard.charts.weekly_growth.positive", {
                  weekly: trend.averageWeeklyGrowth.toFixed(1),
                })
              : t("dashboard.charts.weekly_growth.negative", {
                  weekly: trend.averageWeeklyGrowth.toFixed(1),
                })}
          </div>
        </div>
      )}
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData} margin={{ top: 40, right: 30, left: -35, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
          <XAxis
            dataKey="date"
            tick={(props: GrowthTickProps) => <GrowthTickContent {...props} timeFilter={timeFilter} />}
            axisLine={{ stroke: document.documentElement.classList.contains("dark") ? "#9ca3af" : "#6b7280" }}
            tickLine={{ stroke: document.documentElement.classList.contains("dark") ? "#9ca3af" : "#6b7280" }}
            interval={0}
            height={60}
          />
          <YAxis
            tick={{ fontSize: 13, fill: document.documentElement.classList.contains("dark") ? "#9ca3af" : "#6b7280" }}
            axisLine={{ stroke: document.documentElement.classList.contains("dark") ? "#9ca3af" : "#6b7280" }}
            tickLine={{ stroke: document.documentElement.classList.contains("dark") ? "#9ca3af" : "#6b7280" }}
          />
          <Tooltip content={<GrowthTooltipContent />} />
          <Line type="monotone" dataKey="users" stroke="#3B82F6" strokeWidth={3} dot={<CustomDot />} activeDot={<CustomActiveDot />}>
            <LabelList
              dataKey="change"
              position="top"
              offset={20}
              content={<GrowthLabelContent />}
            />
          </Line>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
